import type { QueryClient } from '@tanstack/react-query';

/** Display order and labels for the backend's Role keys (metacommand/deck/build_roles.py). */
/** The gate on a NONLAND add without a cut: a free slot, with the land floor's slots kept (`min(room, free_slots)`).
 *  A land add needs only `free_slots`. One definition of full for every door (backend `build_fill.nonland_room`). */
export function nonlandRoom(b: { room: number; free_slots: number }): number {
  return Math.min(b.room, b.free_slots);
}

export const ROLE_ORDER = ['land', 'ramp', 'draw', 'interaction', 'removal', 'wipe', 'counter', 'protection', 'tutor', 'recursion', 'theme', 'wincon'] as const;

export const ROLE_LABELS: Record<string, string> = {
  land: 'Lands',
  ramp: 'Ramp',
  draw: 'Card draw',
  interaction: 'Interaction',
  removal: 'Removal',
  wipe: 'Board wipes',
  counter: 'Counterspells',
  protection: 'Protection',
  tutor: 'Tutors',
  recursion: 'Recursion',
  theme: 'Theme',
  wincon: 'Win conditions',
};

/** Parent buckets the backend may send alongside their child roles (build_roles.GROUP_MEMBERS). When a payload carries a
 *  parent row, its children are folded into it: a child row at target 0 is not a slot of its own, and a child slider there
 *  would commit a delta against the child's v1 default and LOWER the parent while the row still read 0. Driven by what the
 *  wire sends, never by a frontend template-version flag. */
export const ROLE_GROUP_CHILDREN: Record<string, readonly string[]> = {
  interaction: ['removal', 'wipe', 'counter'],
};

/** The role rows the Build tab lists. The Theme row is never one: each chosen theme is its own section above the roles
 *  (the template keeps the row: its target still sets the even shares). */
export function visibleRoles<T extends { role: string; target: number }>(roles: readonly T[]): T[] {
  const present = new Set(roles.map((r) => r.role));
  const folded = new Set(
    Object.entries(ROLE_GROUP_CHILDREN).filter(([parent]) => present.has(parent)).flatMap(([, kids]) => kids),
  );
  return roles.filter((r) => r.role !== 'theme' && !(folded.has(r.role) && r.target === 0));
}

/** One line of copy per target bracket for the picker. Copy only — every rule
 *  (game-changer cap, land tiers, combo gate) is computed by the backend. */
export const BRACKET_BLURBS: Record<number, string> = {
  1: 'Precon-level decks played for the experience.',
  2: 'Upgraded precons; no game changers.',
  3: 'Tuned decks with a few game changers.',
  4: 'Optimized decks with no restrictions.',
  5: 'Competitive EDH.',
};

/** Why a fill stopped short, in a few words (the backend's stop reasons). */
export const STOPPED_TEXT: Record<string, string> = {
  'full': 'Stopped: the deck is full.',
  'no members': 'Stopped: nothing left to place.',
  'theme ran out': 'Stopped: the theme ran out of cards.',
  'row ran out': 'Stopped: the row ran out of cards.',
  'gap closed': 'Nothing to fill: every gap is already closed.',
};

/** Every builder read depends on the build's list; invalidate them together after any write to it. */
export function invalidateBuild(
  qc: QueryClient, deckId: string, buildId: string, opts: { payloadApplied?: boolean } = {},
): Promise<void> {
  return Promise.all([
    qc.invalidateQueries({ queryKey: ['deck', deckId] }),
    // A write that already set the build payload from its own response must not refetch it, but every other read
    // under the `build` prefix (e.g. the bracket-fit card) still refreshes: its key is longer than the payload's.
    qc.invalidateQueries({
      queryKey: ['build', deckId, buildId],
      predicate: opts.payloadApplied ? (q) => q.queryKey.length > 3 : undefined,
    }),
    qc.invalidateQueries({ queryKey: ['buildSuggest', deckId, buildId] }),
    qc.invalidateQueries({ queryKey: ['buildLands', deckId, buildId] }),
    qc.invalidateQueries({ queryKey: ['buildCombos', deckId, buildId] }),
    qc.invalidateQueries({ queryKey: ['buildTuning', deckId, buildId] }),
    qc.invalidateQueries({ queryKey: ['goalsInspect', deckId, buildId] }),
    // A theme's cut order reads the list, the stars and the role rows (a role slider changes who is protected).
    qc.invalidateQueries({ queryKey: ['themeCutOrder', deckId, buildId] }),
    // Any write to the list changes who can pay for a card: the swap proposal reads it.
    qc.invalidateQueries({ queryKey: ['swapProposal', deckId, buildId] }),
    qc.invalidateQueries({ queryKey: ['cardMeta', deckId] }),
    qc.invalidateQueries({ queryKey: ['builds', deckId] }),
    qc.invalidateQueries({ queryKey: ['variant', buildId] }),
  ]).then(() => undefined);
}
